import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ProductDataComponent } from '../product-data/product-data.component';
import { InsuranceDetailsComponent } from '@base/components/insurance-details/insurance-details.component';
import { PersonDataComponent } from '@base/components/person-data/person-data.component';
import { PaymentDataComponent } from '@base/components/payment-data/payment-data.component';
import { LegalChecksComponent } from '../legal-checks/legal-checks.component';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { checkForPaymentInfoInUrl, checkForPersonDataInUrl } from '@base/app/shared/utils/check-for-data-in-url.helper';
import { checkForProductDataInUrl } from '../../utils/check-for-product-data-in-url.helper';
import { createKaufpreisForm, KaufpreisFormModel } from '../../factories/kaufpreis-form.factory';
import { KaufpreisService } from '../../services/kaufpreis.service';
import {
  BrokernetLanguageType,
  BrokernetProductName, Bundesland,
  PaymentType,
  PublicPersonCreateDto,
  PublicServoKaufpreisKaskoContractCreateDto,
  PublicServoKaufpreisKaskoLegalChecksDto,
  PublicServoKaufpreisKaskoProductDetailsDto
} from '@base/backend';
import { getAdvertiserInfo, getPublicLinkCustomerDto } from '@base/app/shared/utils/dto-builder.helper';
import { tap } from 'rxjs';
import { ServoProductNameTranslations } from '@base/models/enum/insurance-type.enum';

@Component({
  selector: 'app-kaufpreis-form-wrapper',
  standalone: true,
  imports: [
    ProductDataComponent,
    InsuranceDetailsComponent,
    PersonDataComponent,
    PaymentDataComponent,
    LegalChecksComponent,
    LegalChecksComponent,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './kaufpreis-form-wrapper.component.html',
  styleUrl: './kaufpreis-form-wrapper.component.scss'
})
export class KaufpreisFormWrapperComponent implements OnInit {
  private kaufpreisService = inject(KaufpreisService);

  protected readonly ServoProductNameTranslations = ServoProductNameTranslations;

  @Input() price!: number;
  @Input() premium!: number;
  @Input() servoAdvertiserId: string | null = null;
  @Input() isPublic: boolean = false;

  @Output() submitted = new EventEmitter<void>();

  mileageToHigh = false;
  objectToOld = false;
  isLoading = false;

  form!: FormGroup<KaufpreisFormModel>;

  ngOnInit() {
    this.form = createKaufpreisForm();

    const productCtrls = this.form.controls.product.controls;

    productCtrls.mileage.valueChanges
      .subscribe(mileage => this.mileageToHigh = mileage !== null && mileage > 200000);

    productCtrls.dateOfFirstRegistration.valueChanges
      .subscribe(age => {
        if (age === null) {
          this.objectToOld = false;
          return;
        }
        const tenYearsAgo = new Date();
        tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
        this.objectToOld = new Date(age) ! <= tenYearsAgo;
      });

    this.checkForDataInUrl();
  }

  checkForDataInUrl(): void {
    const url: URL = new URL(window.location.href);
    const personForm = this.form.controls.person.controls;
    const paymentForm = this.form.controls.payment.controls;
    const productForm = this.form.controls.product.controls;

    checkForPersonDataInUrl(url, personForm);
    checkForPaymentInfoInUrl(url, paymentForm);
    checkForProductDataInUrl(url, productForm);
  }

  submit(): void {
    this.form.markAllAsTouched();
    this.isLoading = true;

    if (this.form.invalid) {
      this.isLoading = false;
      return;
    }

    const dto: PublicServoKaufpreisKaskoContractCreateDto = {
      publicPerson: this.isPublic ? this.getPublicPersonCreateDto() : undefined,
      publicServoKaufpreisKaskoProductDetailsDto: this.getProductDetailsDto(),
      publicServoKaufpreisKaskoLegalChecksDto: this.getLegalChecksDto(),
      publicBrokerAdvertiserInfo: this.isPublic ? getAdvertiserInfo(this.servoAdvertiserId) : undefined,
      publicLinkCustomerDto: this.isPublic ? undefined : getPublicLinkCustomerDto()
    };

    this.kaufpreisService.submitKaufpreisContract(dto)
      .pipe(tap(() => console.error('Kaufpreis Kasko onboarding submitted')))
      .subscribe({
        next: () => this.submitted.emit(),
        complete: () => this.isLoading = false
      });
  }

  private getPublicPersonCreateDto(): PublicPersonCreateDto {
    const rawPersonData = this.form.controls.person.getRawValue();
    const paymentData = this.form.controls.payment.getRawValue();

    return {
      firstname: rawPersonData.firstname!,
      lastname: rawPersonData.lastname!,
      publicAddressCreateDto: {
        city: rawPersonData.address.city!,
        country: rawPersonData.address.country!,
        houseNumber: rawPersonData.address.houseNumber!,
        street: rawPersonData.address.street!,
        zip: rawPersonData.address.zip!,
        bundesland: Bundesland.Oberoesterreich
      },
      publicContactInfoCreateDto: {
        email: rawPersonData.email!,
        phone: rawPersonData.phone
      },
      publicPaymentInfoCreateDto: {
        iban: paymentData.iban!,
        paymentPeriod: paymentData.paymentPeriod!,
        paymentType: PaymentType.Sepa
      },
      brokernetLanguageType: BrokernetLanguageType.De
    }
  }

  private getProductDetailsDto(): PublicServoKaufpreisKaskoProductDetailsDto {
    const rawProductData = this.form.controls.product.getRawValue();

    return {
      brand: rawProductData.brand!,
      dateOfFirstRegistration: rawProductData.dateOfFirstRegistration!,
      model: rawProductData.model!,
      mileage: rawProductData.mileage!,
      publicServoKaufpreisKaskoVersicherungspraemieCalcDto: {
        kaufpreisInEur: this.price
      },
      fin: rawProductData.fin!,
      servoKaufpreisKaskoKraftfahrzeugType: rawProductData.servoKaufpreisKaskoKraftfahrzeugType!
    };
  }

  private getLegalChecksDto(): PublicServoKaufpreisKaskoLegalChecksDto {
    const rawLegalChecksData = this.form.controls.legalCheck.getRawValue();

    return {
      hasAgreedToElectronicCommunication: rawLegalChecksData.hasAgreedToElectronicCommunication,
      hasAcceptedPrivacyPolicy: rawLegalChecksData.hasAcceptedPrivacyPolicy,
      hasAcceptedGeneralTermsAndConditions: rawLegalChecksData.hasAcceptedGeneralTermsAndConditions,
      hasReadInsuranceProductInformationSheet: rawLegalChecksData.hasReadInsuranceProductInformationSheet,
      hasAuthorizedSepaDirectDebit: rawLegalChecksData.hasAuthorizedSepaDirectDebit,
      hasAcknowledgedRightOfWithdrawal: rawLegalChecksData.hasAcknowledgedRightOfWithdrawal,
      hasConfirmedProductMeetsWishesAndNeeds: rawLegalChecksData.hasConfirmedProductMeetsWishesAndNeeds,
      hatZusatzversicherungZuVollkaskoAkzeptiert: rawLegalChecksData.hasConfirmedVollkaskoInsuranceInAustria,
      hatGesamtgewichtBisFuehrerscheinKlasseBAkzeptiert: rawLegalChecksData.hasConfirmedVehicleWeightBelow_35_T,
      hatAltersbegrenzung10JahreAkzeptiert: rawLegalChecksData.hasAcknowledgedCoverageEndAfterTenYears,
      hatKeineGewerblicheNutzungAkzeptiert: rawLegalChecksData.hasConfirmedNoCommercialUse,
      hatLaufleistungsbegrenzung200000KmAkzeptiert: rawLegalChecksData.hasAcknowledgedCoverageEndAfter_200_000_Km
    }
  }

  protected readonly BrokernetProductName = BrokernetProductName;
}
